import React, { useCallback, useEffect, useRef, useState } from "react";
import { Box, Badge, Button, Flex, Select, Text, useToast } from "@chakra-ui/react";
import { agentGet, agentPost } from "../../utils/apis/agentApi";
import JevTiming from "./JevTiming";

/** Advisory records only; reviewing one never resolves its underlying flag. */
export default function JevAssessments() {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState<any>(null);
  const [busy, setBusy] = useState(false);
  const [labels, setLabels] = useState<Record<number, string>>({});
  const generation = useRef(0);
  const mutation = useRef(false);
  const toast = useToast();
  const load = useCallback(async () => {
    const version = ++generation.current;
    setBusy(true);
    try {
      const res = await agentGet("api/jev");
      if (version !== generation.current) return;
      if (!res.ok) throw new Error("unavailable");
      setData(res.data);
    } catch {
      if (version === generation.current) {
        setData(null);
        toast({ title: "Assessments unavailable", description: "The review queue remains available.", status: "error" });
      }
    } finally {
      if (version === generation.current && !mutation.current) setBusy(false);
    }
  }, [toast]);
  useEffect(() => { if (open) load(); return () => { generation.current += 1; }; }, [open, load]);
  const review = async (row: any, action: string) => {
    if (mutation.current) return;
    mutation.current = true;
    generation.current += 1;
    setBusy(true);
    try {
      const res = await agentPost(`api/jev/${row.id}/review`, {
        revision: row.revision, action, label: action === "correct" ? labels[row.id] : null,
      });
      if (!res.ok) toast({ title: res.status === 409 ? "Assessment changed; refreshed" : "Could not save review", status: "error" });
    } catch {
      toast({ title: "Review not confirmed", description: "Refresh to check before trying again.", status: "error" });
    } finally {
      try { await load(); } finally { mutation.current = false; setBusy(false); }
    }
  };
  const readable = (value: string) => value.replace(/_/g, " ");
  return <Box mt={6} borderTopWidth="1px" pt={4}>
    <JevTiming />
    <Button variant="outline" onClick={() => setOpen(v => !v)} aria-expanded={open}>Jev assessment review</Button>
    {open && <Box mt={3}>
      <Text fontSize="sm">Staff review of a suggestion does not resolve an issue or change a customer request.</Text>
      <Button size="sm" my={2} isDisabled={busy} onClick={load}>Refresh assessments</Button>
      {data?.settings?.timing_shadow_enabled && <Text fontSize="sm">
        Limited English timing shadow is configured. Eligible short replies may be compared with Jev while the approved campaign is active. Existing decisions remain unchanged. Open Jev timing comparison for results.
      </Text>}
      {!data?.settings?.timing_shadow_enabled && data?.settings?.evaluation_scope === "frozen_synthetic_only" && <Text fontSize="sm">
        Synthetic evaluation only. Customer observation and production changes are blocked in this release, including when a key is configured.
      </Text>}
      {data && <Text fontSize="sm">External evaluation: {data.settings.live_api_allowed ? "configured" : "off"}.
        {" "}Runtime checks: {data.settings.runtime_enabled ? `configured for ${data.settings.runtime_percentage || 0}% of conversations` : "off"}.
        {" "}Background evaluation: {data.settings.background_enabled ? "configured" : "off"}.
        {" "}Each intervention also requires a current data approval and calibrated policy.</Text>}
      {data?.assessments?.length === 0 && <Text>No assessments recorded.</Text>}
      {data?.assessments?.map((row: any) => {
        const answers = row.result?.answers || {};
        const actionable = row.mode === "suggest" && row.status === "ok" && row.result?.decision === "staff_suggestion";
        return <Box key={row.id} borderWidth="1px" p={3} my={2} borderRadius="md">
          <Flex gap={2} wrap="wrap"><Badge colorScheme="purple">{row.review ? "Reviewed by staff" : actionable ? "Suggested by Jev" : row.mode === "enforce" ? "Runtime assessment" : "Evaluation only"}</Badge>
            <Badge>{row.status}</Badge><Text>{readable(row.workstream)}</Text></Flex>
          <Text fontSize="sm">{row.owner} · {row.model} · {row.bundle_version} · SDK {row.result?.sdk || "pending"}</Text>
          <Text fontSize="sm">{row.flag_id ? `Review issue #${row.flag_id}` : `Conversation message #${row.source_id}`}</Text>
          <Button as="a" size="sm" variant="link" href={`/conversations?phone=${encodeURIComponent(row.owner)}`}>Open conversation</Button>
          {answers.issue && <Text>Suggested issue: {readable(answers.issue.choice)}</Text>}
          {typeof answers.urgency?.score === "number" && <Text>Suggested urgency: {answers.urgency.score.toFixed(2)} / 2 (assessment, not a confirmed incident)</Text>}
          {row.result?.failure && <Text>Assessment unavailable: {readable(row.result.failure)}</Text>}
          {row.review && <Text>Staff review: {row.review.action}{row.review.label ? ` — ${row.review.label}` : ""} · {row.review.actor}</Text>}
          <details><summary>Versions and probabilities</summary><pre style={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>{JSON.stringify(row.result, null, 2)}</pre></details>
          {actionable && <Flex gap={2} mt={2} wrap="wrap">
            <Button size="sm" isDisabled={busy} onClick={() => review(row, "confirm")}>Confirm suggestion</Button>
            {row.workstream === "admin_triage" && <Select maxW="240px" aria-label={`Correct assessment ${row.id}`} value={labels[row.id] || ""}
              onChange={e => setLabels(v => ({ ...v, [row.id]: e.target.value }))}>
              <option value="">Choose corrected issue</option>{data.labels.map((s: string) => <option key={s} value={s}>{readable(s)}</option>)}
            </Select>}
            {row.workstream === "admin_triage" && <Button size="sm" isDisabled={busy || !labels[row.id]} onClick={() => review(row, "correct")}>Save correction</Button>}
            <Button size="sm" isDisabled={busy} onClick={() => review(row, "dismiss")}>Dismiss suggestion</Button>
          </Flex>}
        </Box>;
      })}
    </Box>}
  </Box>;
}
